// components/sections/DomainSkillsSection.js - VERSION AVEC LOGIQUE ORDRE COMPLÈTE
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ChevronDown, ChevronUp, Edit3, GraduationCap, GripVertical, Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Hammer, Check, Square } from 'lucide-react';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { SkillCard } from './SkillCard';
import { SkillDataEditor } from './SkillDataEditor';

export const DomainSkillsSection = ({
    selectedDomain,
    categories,
    customSkillDomains,
    skillsByDomain,
    skillsData = {},
    onOpenSkillsModal,
    onRemoveSkill,
    onReorderSkills,
    onUpdateSkillData,
    onUpdateSkillWithNameChange,
    onRateSkill,
    domainesData = [],
    skillsDataRaw = [],
    refreshTrigger
}) => {
    const [draggedItem, setDraggedItem] = useState(null);
    const [expandedSkills, setExpandedSkills] = useState(new Set());
    const [editingSkill, setEditingSkill] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(7);
    const [sortBy, setSortBy] = useState('none'); // Par défaut : ordre naturel
    const [sortDirection, setSortDirection] = useState('desc');
    const [forceRender, setForceRender] = useState(0);
    const [localSkillsData, setLocalSkillsData] = useState({});
    const [selectedSkills, setSelectedSkills] = useState(new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [localSkillsByDomain, setLocalSkillsByDomain] = useState({});

    // Synchroniser avec les props
    useEffect(() => {
        setLocalSkillsData(skillsData);
    }, [skillsData, refreshTrigger]);

    useEffect(() => {
        setLocalSkillsByDomain(skillsByDomain);
    }, [skillsByDomain, refreshTrigger]);

    // Fonction pour obtenir les informations du domaine sélectionné
    const getSelectedDomainInfo = useCallback(() => {
        if (!selectedDomain) return null;

        let domain = domainesData.find(d => d.id_domaine === selectedDomain);
        if (domain) {
            return {
                id: domain.id_domaine,
                name: domain.nom_domaine,
                icon: domain.icon || '👤',
                description: domain.description,
                isCustom: domain.type_domaine === 'custom'
            };
        }

        domain = categories.find(cat => cat.id === selectedDomain);
        if (domain) return { ...domain, isCustom: false };

        const customIndex = customSkillDomains.findIndex((_, index) => `custom-${index}` === selectedDomain);
        if (customIndex >= 0) {
            return {
                id: selectedDomain,
                name: customSkillDomains[customIndex],
                icon: '👤',
                isCustom: true
            };
        }

        return null;
    }, [selectedDomain, domainesData, categories, customSkillDomains, refreshTrigger]);

    // MODIFIÉ : Fonction pour obtenir les compétences avec la nouvelle logique d'ordre
    const getSkillsForSelectedDomain = useCallback(() => {
        const skills = localSkillsByDomain[selectedDomain] || skillsByDomain[selectedDomain] || [];
        
        if (sortBy === 'none') {
            // Vérifier si des ordre_affichage sont définis (drag & drop personnalisé)
            const hasCustomOrder = skills.some(skill => {
                const skillData = skillsData[skill] || localSkillsData[skill] || {};
                return skillData.ordre_affichage && skillData.ordre_affichage > 0;
            });
            
            if (hasCustomOrder) {
                // Utiliser l'ordre drag & drop personnalisé
                return [...skills].sort((a, b) => {
                    const skillDataA = skillsData[a] || localSkillsData[a] || {};
                    const skillDataB = skillsData[b] || localSkillsData[b] || {};
                    const ordreA = skillDataA.ordre_affichage || 999999; // Skills sans ordre à la fin
                    const ordreB = skillDataB.ordre_affichage || 999999;
                    return ordreA - ordreB;
                });
            } else {
                // Utiliser l'ordre original du JSON par défaut
                return skills;
            }
        }

        // Tri par rating
        if (sortBy === 'rating') {
            return [...skills].sort((a, b) => {
                const skillDataA = skillsData[a] || localSkillsData[a] || {};
                const skillDataB = skillsData[b] || localSkillsData[b] || {};
                const ratingA = skillDataA.rating || 0;
                const ratingB = skillDataB.rating || 0;
                return sortDirection === 'desc' ? ratingB - ratingA : ratingA - ratingB;
            });
        }

        // Tri par difficulté
        if (sortBy === 'difficulty') {
            return [...skills].sort((a, b) => {
                const skillDataA = skillsData[a] || localSkillsData[a] || {};
                const skillDataB = skillsData[b] || localSkillsData[b] || {};
                const difficultyOrder = { 'Débutant': 1, 'Intermédiaire': 2, 'Avancé': 3, 'Expert': 4 };
                const diffA = difficultyOrder[skillDataA.niveau_maitrise_requis] || 0;
                const diffB = difficultyOrder[skillDataB.niveau_maitrise_requis] || 0;
                return sortDirection === 'asc' ? diffA - diffB : diffB - diffA;
            });
        }

        return skills;
    }, [selectedDomain, localSkillsByDomain, skillsByDomain, sortBy, sortDirection, skillsData, localSkillsData, refreshTrigger]);

    const allSkills = getSkillsForSelectedDomain();

    const paginatedSkills = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return allSkills.slice(startIndex, endIndex);
    }, [allSkills, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(allSkills.length / itemsPerPage);

    // Handlers pour tri et pagination
    const handleSortClick = (sortType) => {
        if (sortBy === sortType) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(sortType);
            setSortDirection(sortType === 'rating' ? 'desc' : 'asc');
        }
    };

    const getSortIcon = (sortType) => {
        if (sortBy !== sortType) {
            return <ArrowUpDown className="w-3 h-3" />;
        }
        return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        setExpandedSkills(new Set());
        setEditingSkill(null);
        setSelectedSkills(new Set());
    };

    // Handlers pour la gestion des compétences
    const handleRemoveSkill = (skillName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la compétence "${skillName}" ? Cette action est irréversible.`)) return;

        setLocalSkillsByDomain(prev => ({
            ...prev,
            [selectedDomain]: (prev[selectedDomain] || []).filter(skill => skill !== skillName)
        }));

        if (onRemoveSkill && typeof onRemoveSkill === 'function') {
            onRemoveSkill(skillName);
        }
    };

    const handleRemoveSelectedSkills = () => {
        if (selectedSkills.size === 0) return;

        const skillsToDelete = Array.from(selectedSkills);
        const confirmMessage = skillsToDelete.length === 1
            ? `Êtes-vous sûr de vouloir supprimer la compétence "${skillsToDelete[0]}" ?`
            : `Êtes-vous sûr de vouloir supprimer ces ${skillsToDelete.length} compétences ?\n\n${skillsToDelete.join(', ')}`;

        if (!window.confirm(confirmMessage + '\n\nCette action est irréversible.')) return;

        setLocalSkillsByDomain(prev => ({
            ...prev,
            [selectedDomain]: (prev[selectedDomain] || []).filter(skill => !skillsToDelete.includes(skill))
        }));

        skillsToDelete.forEach(skillName => {
            if (onRemoveSkill && typeof onRemoveSkill === 'function') {
                onRemoveSkill(skillName);
            }
        });

        setSelectedSkills(new Set());
        setIsSelectionMode(false);
    };

    // Handlers pour la sélection multiple
    const toggleSkillSelection = (skillName) => {
        const newSelection = new Set(selectedSkills);
        if (newSelection.has(skillName)) {
            newSelection.delete(skillName);
        } else {
            newSelection.add(skillName);
        }
        setSelectedSkills(newSelection);

        if (newSelection.size === 0) {
            setIsSelectionMode(false);
        }
    };

    const selectAllVisibleSkills = () => {
        const newSelection = new Set(selectedSkills);
        paginatedSkills.forEach(skill => newSelection.add(skill));
        setSelectedSkills(newSelection);
    };

    const deselectAllSkills = () => {
        setSelectedSkills(new Set());
        setIsSelectionMode(false);
    };

    const enterSelectionMode = () => {
        setIsSelectionMode(true);
        setSelectedSkills(new Set());
    };

    // MODIFIÉ : Handler drag and drop avec mise à jour ordre_affichage
    const handleDragEnd = useCallback((result) => {
        const { destination, source } = result;

        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            setDraggedItem(null);
            return;
        }

        // Forcer le mode 'none' après drag & drop
        setSortBy('none');

        const currentSkills = localSkillsByDomain[selectedDomain] || skillsByDomain[selectedDomain] || [];
        const reorderedSkills = Array.from(currentSkills);
        const sourceGlobalIndex = (currentPage - 1) * itemsPerPage + source.index;
        const destGlobalIndex = (currentPage - 1) * itemsPerPage + destination.index;

        const [movedSkill] = reorderedSkills.splice(sourceGlobalIndex, 1);
        reorderedSkills.splice(destGlobalIndex, 0, movedSkill);

        // Mise à jour locale immédiate
        setLocalSkillsByDomain(prev => ({
            ...prev,
            [selectedDomain]: reorderedSkills
        }));

        // NOUVEAU : Mettre à jour les ordre_affichage pour sauvegarder l'ordre drag & drop
        const updatedSkillsData = { ...localSkillsData };
        reorderedSkills.forEach((skillName, index) => {
            if (!updatedSkillsData[skillName]) {
                updatedSkillsData[skillName] = {};
            }
            updatedSkillsData[skillName].ordre_affichage = index + 1;
        });
        setLocalSkillsData(updatedSkillsData);

        setDraggedItem(null);

        // Appel API en arrière-plan
        if (onReorderSkills && typeof onReorderSkills === 'function') {
            setTimeout(() => {
                onReorderSkills(selectedDomain, reorderedSkills);
            }, 0);
        }
    }, [selectedDomain, localSkillsByDomain, skillsByDomain, currentPage, itemsPerPage, localSkillsData, onReorderSkills]);

    const handleDragStart = useCallback((start) => {
        setDraggedItem(start.draggableId);
    }, []);

    const handleDragUpdate = useCallback((update) => {
        // Optimisation : éviter les re-renders pendant le drag
    }, []);

    // Handlers pour l'expansion et la notation
    const toggleSkillExpanded = useCallback((skillName) => {
        setExpandedSkills(prev => {
            const newExpanded = new Set(prev);
            newExpanded.has(skillName) ? newExpanded.delete(skillName) : newExpanded.add(skillName);
            return newExpanded;
        });
    }, []);

    const handleRateSkill = useCallback((skillName, rating) => {
        const currentData = skillsData[skillName] || localSkillsData[skillName] || {};
        const updatedData = {
            description: currentData.description || '',
            training_link: currentData.training_link || '',
            certification: currentData.certification || '',
            niveau_maitrise_requis: currentData.niveau_maitrise_requis || 'Débutant',
            ordre_affichage: currentData.ordre_affichage || 0, // Conserver ordre_affichage
            rating: rating
        };

        setLocalSkillsData(prev => ({
            ...prev,
            [skillName]: updatedData
        }));

        // Appel API en arrière-plan
        if (onRateSkill && typeof onRateSkill === 'function') {
            setTimeout(() => {
                onRateSkill(skillName, rating);
            }, 0);
        } else if (onUpdateSkillData && typeof onUpdateSkillData === 'function') {
            setTimeout(() => {
                onUpdateSkillData(skillName, updatedData);
            }, 0);
        }
    }, [skillsData, localSkillsData, onRateSkill, onUpdateSkillData]);

    const handleButtonClick = () => {
        if (onOpenSkillsModal && typeof onOpenSkillsModal === 'function') {
            onOpenSkillsModal();
        }
    };

    // Handler pour la sauvegarde avec changement de nom
    const handleSkillSave = useCallback((oldSkillName, newSkillData) => {
        const hasNameChanged = oldSkillName !== newSkillData.name;

        if (hasNameChanged) {
            if (onUpdateSkillWithNameChange && typeof onUpdateSkillWithNameChange === 'function') {
                onUpdateSkillWithNameChange(oldSkillName, newSkillData).then(result => {
                    if (result && result.success) {
                        setLocalSkillsByDomain(prev => ({
                            ...prev,
                            [selectedDomain]: (prev[selectedDomain] || []).map(skill => 
                                skill === oldSkillName ? newSkillData.name : skill
                            )
                        }));

                        setLocalSkillsData(prev => {
                            const newData = { ...prev };
                            delete newData[oldSkillName];
                            newData[newSkillData.name] = {
                                description: newSkillData.description,
                                training_link: newSkillData.training_link,
                                certification: newSkillData.certification,
                                niveau_maitrise_requis: newSkillData.niveau_maitrise_requis,
                                ordre_affichage: prev[oldSkillName]?.ordre_affichage || 0 // Conserver ordre
                            };
                            return newData;
                        });

                        if (selectedSkills.has(oldSkillName)) {
                            setSelectedSkills(prev => {
                                const newSelection = new Set(prev);
                                newSelection.delete(oldSkillName);
                                newSelection.add(newSkillData.name);
                                return newSelection;
                            });
                        }

                        if (expandedSkills.has(oldSkillName)) {
                            setExpandedSkills(prev => {
                                const newExpanded = new Set(prev);
                                newExpanded.delete(oldSkillName);
                                newExpanded.add(newSkillData.name);
                                return newExpanded;
                            });
                        }
                    }
                });
            } else {
                console.warn('onUpdateSkillWithNameChange n\'est pas défini');
            }
        } else {
            const currentData = localSkillsData[oldSkillName] || {};
            setLocalSkillsData(prev => ({
                ...prev,
                [oldSkillName]: {
                    description: newSkillData.description,
                    training_link: newSkillData.training_link,
                    certification: newSkillData.certification,
                    niveau_maitrise_requis: newSkillData.niveau_maitrise_requis,
                    ordre_affichage: currentData.ordre_affichage || 0, // Conserver ordre
                    rating: currentData.rating || 0 // Conserver rating
                }
            }));

            if (onUpdateSkillData && typeof onUpdateSkillData === 'function') {
                onUpdateSkillData(oldSkillName, {
                    description: newSkillData.description,
                    training_link: newSkillData.training_link,
                    certification: newSkillData.certification,
                    niveau_maitrise_requis: newSkillData.niveau_maitrise_requis
                });
            }
        }
    }, [selectedDomain, onUpdateSkillWithNameChange, onUpdateSkillData, selectedSkills, expandedSkills, localSkillsData]);

    // Composant PaginationControls
    const PaginationControls = () => (
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
            <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Précédent
            </button>
            <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500 mr-2">
                    {allSkills.length} compétence{allSkills.length > 1 ? 's' : ''}
                    {selectedSkills.size > 0 && (
                        <span className="ml-1 text-[#18574F] font-medium">
                            ({selectedSkills.size} sélectionnée{selectedSkills.size > 1 ? 's' : ''})
                        </span>
                    )}
                </span>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                    <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            currentPage === pageNumber
                                ? 'bg-[#d9efeb] text-gray-800'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        {pageNumber}
                    </button>
                ))}
            </div>
            <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Suivant
            </button>
        </div>
    );

    const domainInfo = getSelectedDomainInfo();

    if (selectedDomain && currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
            {/* Header */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-700">
                        <h2 className="text-sm font-semibold">Compétences</h2>
                    </div>
                    <button
                        onClick={handleButtonClick}
                        disabled={!selectedDomain}
                        className="px-3 py-1 bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter des compétences
                    </button>
                </div>
            </div>

            {selectedDomain && domainInfo ? (
                <div className="flex-1 flex flex-col">
                    {/* Controls */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-[#c8d7d6] rounded-md px-3 py-2">
                            <span className="font-medium text-gray-900 text-sm">
                                {domainInfo.name}
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* Boutons de tri */}
                            <button
                                onClick={() => handleSortClick('difficulty')}
                                disabled={isSelectionMode}
                                className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                    sortBy === 'difficulty'
                                        ? 'bg-gray-200 text-gray-800 border-gray-300'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                } ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {getSortIcon('difficulty')}
                                <span>Difficulté</span>
                            </button>
                            <button
                                onClick={() => handleSortClick('rating')}
                                disabled={isSelectionMode}
                                className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                    sortBy === 'rating'
                                        ? 'bg-gray-200 text-gray-800 border-gray-300'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                } ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {getSortIcon('rating')}
                                <span>Rating</span>
                            </button>
                            <button
                                onClick={() => setSortBy('none')}
                                disabled={isSelectionMode}
                                className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                    sortBy === 'none'
                                        ? 'bg-gray-200 text-gray-800 border-gray-300'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                } ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <GripVertical className="w-3 h-3" />
                                <span>Position</span>
                            </button>

                            {/* Boutons de sélection multiple */}
                            {allSkills.length > 0 && (
                                <>
                                    <div className="w-px h-6 bg-gray-300"></div>
                                    {!isSelectionMode ? (
                                        <button
                                            onClick={enterSelectionMode}
                                            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                        >
                                            <Square className="w-3 h-3" />
                                            <span>Sélectionner</span>
                                        </button>
                                    ) : (
                                        <div className="flex items-center space-x-1">
                                            <button
                                                onClick={selectAllVisibleSkills}
                                                className="flex items-center space-x-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                            >
                                                <Check className="w-3 h-3" />
                                                <span>Tout</span>
                                            </button>
                                            <button
                                                onClick={deselectAllSkills}
                                                className="px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                            >
                                                Annuler
                                            </button>
                                            {selectedSkills.size > 0 && (
                                                <button
                                                    onClick={handleRemoveSelectedSkills}
                                                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                    <span>Supprimer ({selectedSkills.size})</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Skills List */}
                    {allSkills.length > 0 ? (
                        <>
                            <div className="flex-1 min-h-0">
                                <div className="h-full flex flex-col">
                                    <div className="flex-1">
                                        <DragDropContext
                                            onDragEnd={handleDragEnd}
                                            onDragStart={handleDragStart}
                                            onDragUpdate={handleDragUpdate}
                                        >
                                            <Droppable
                                                droppableId={`skills-${selectedDomain}`}
                                                isDropDisabled={sortBy !== 'none' || isSelectionMode}
                                                renderClone={(provided, snapshot, rubric) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="group bg-white rounded-lg border-2 border-[#18574F] shadow-lg rotate-1 scale-105 transition-none"
                                                    >
                                                        <div className="flex items-center justify-between p-2">
                                                            <span className="text-gray-900 font-medium text-sm">
                                                                {paginatedSkills[rubric.source.index]}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                        className={`space-y-2 transition-all duration-200 ${
                                                            snapshot.isDraggingOver ? 'bg-[#18574F]/5 rounded-lg p-2' : ''
                                                        }`}
                                                    >
                                                        {paginatedSkills.map((skill, index) => (
                                                            <SkillCard
                                                                key={skill}
                                                                skill={skill}
                                                                index={index}
                                                                skillData={localSkillsData[skill] || skillsData[skill] || {}}
                                                                isExpanded={expandedSkills.has(skill)}
                                                                isSelected={selectedSkills.has(skill)}
                                                                isSelectionMode={isSelectionMode}
                                                                isDragDisabled={sortBy !== 'none' || isSelectionMode}
                                                                draggedItem={draggedItem}
                                                                sortBy={sortBy}
                                                                onToggleExpanded={toggleSkillExpanded}
                                                                onToggleSelection={toggleSkillSelection}
                                                                onRate={handleRateSkill}
                                                                onEdit={setEditingSkill}
                                                                onRemove={handleRemoveSkill}
                                                            />
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    </div>
                                    {totalPages > 1 && <div className="flex-shrink-0"><PaginationControls /></div>}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 text-gray-500 h-full flex flex-col items-center justify-center">
                            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg font-medium mb-2">Aucune compétence définie</p>
                            <p className="text-sm mb-4">
                                Cliquez sur "Ajouter des compétences" pour commencer à vous former sur le domaine sélectionné
                            </p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-12 text-gray-500 flex-1 flex flex-col items-center justify-center">
                    <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">Sélectionnez un domaine</p>
                    <p className="text-sm">
                        Choisissez un domaine dans la section de gauche pour voir et gérer ses compétences
                    </p>
                </div>
            )}

            {/* Modal d'édition */}
            {editingSkill && (
                <SkillDataEditor
                    skillName={editingSkill}
                    data={localSkillsData[editingSkill] || skillsData[editingSkill] || {}}
                    onSave={handleSkillSave}
                    onCancel={() => setEditingSkill(null)}
                />
            )}
        </div>
    );
};